import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Search, Download, Eye, RefreshCw, CheckCircle, XCircle, Award, X, Clock, BarChart2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MarkSheetsTab({ token }) {
  const [transcripts, setTranscripts] = useState([]);
  const [filteredTranscripts, setFilteredTranscripts] = useState([]);
  const [marksheets, setMarksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [transcriptsRes, marksheetsRes] = await Promise.all([
        axios.get(`${API}/admin/transcripts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/marksheets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTranscripts(transcriptsRes.data);
      setFilteredTranscripts(transcriptsRes.data);
      setMarksheets(marksheetsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  useEffect(() => {
    let result = [...transcripts];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t => 
        t.candidate_name?.toLowerCase().includes(term) ||
        t.candidate_email?.toLowerCase().includes(term) ||
        t.transcript_number?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(t => t.status === statusFilter);
    }
    
    setFilteredTranscripts(result);
  }, [transcripts, searchTerm, statusFilter]);

  const approveTranscript = async (transcriptId) => {
    try {
      await axios.put(`${API}/admin/transcript/${transcriptId}`, 
        { status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transcript approved');
      fetchData();
    } catch (err) {
      toast.error('Failed to approve transcript');
    }
  };

  const issueMarksheet = async (transcriptId) => {
    try {
      await axios.post(`${API}/admin/issue-transcript/${transcriptId}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transcript issued successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to issue transcript');
    }
  };

  const rejectMarksheet = async (transcriptId, reason) => {
    try {
      await axios.post(`${API}/admin/reject-transcript/${transcriptId}?reason=${encodeURIComponent(reason)}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transcript sent back to examiner for revision');
      fetchData();
    } catch (err) {
      toast.error('Failed to reject transcript');
    }
  };

  const downloadMarksheetPDF = async (transcript) => {
    try {
      const response = await axios.get(`${API}/admin/transcript-pdf/${transcript.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IMCAC-Transcript-${transcript.transcript_number || transcript.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Transcript PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const statusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-100 text-blue-700',
      under_review: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      issued: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading transcripts...</p>;

  const pendingCount = transcripts.filter(t => t.status === 'submitted' || t.status === 'under_review').length;
  const issuedCount = transcripts.filter(t => t.status === 'issued').length;
  const approvedCount = transcripts.filter(t => t.status === 'approved').length;

  return (
    <div data-testid="admin-transcripts-tab" className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-3"><p className="text-xs text-slate-500">Total</p><p className="text-xl font-bold text-[#1B2A4A]">{transcripts.length}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Pending Review</p><p className="text-xl font-bold text-amber-600">{pendingCount}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Ready to Issue</p><p className="text-xl font-bold text-green-600">{approvedCount}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Issued</p><p className="text-xl font-bold text-purple-600">{issuedCount}</p></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-50 p-3 rounded-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, transcript number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-sm text-sm"
            />
          </div>
        </div>
        <div className="w-[150px]">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="issued">Issued</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="rounded-sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Transcripts Table */}
      {filteredTranscripts.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center bg-slate-50 rounded">No transcripts found.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">Transcript #</TableHead>
                <TableHead className="text-xs font-bold">Candidate</TableHead>
                <TableHead className="text-xs font-bold">Examination</TableHead>
                <TableHead className="text-xs font-bold">Scores</TableHead>
                <TableHead className="text-xs font-bold">Status</TableHead>
                <TableHead className="text-xs font-bold">Submitted</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTranscripts.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-xs font-mono text-slate-600">{t.transcript_number || '-'}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-[#1B2A4A]">{t.candidate_name}</p>
                    <p className="text-xs text-slate-500">{t.candidate_email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs rounded-sm">{t.examination || 'N/A'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">
                      <p>Theory: {t.subject_scores?.theory || '-'}</p>
                      <p>Practical: {t.practical_score || '-'}</p>
                      <p className="font-semibold">Total: {t.percentage?.toFixed(1) || '-'}%</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs rounded-sm ${statusColor(t.status)}`}>{t.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs rounded-sm"
                        onClick={() => setSelectedTranscript(t)}
                      >
                        <Eye className="h-3 w-3 mr-1" /> View
                      </Button>
                      {['submitted', 'under_review'].includes(t.status) && (
                        <>
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs rounded-sm bg-green-600 text-white"
                            onClick={() => approveTranscript(t.id)}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs rounded-sm border-red-400 text-red-600"
                            onClick={() => {
                              const reason = window.prompt('Rejection reason:');
                              if (reason) rejectMarksheet(t.id, reason);
                            }}
                          >
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {t.status === 'approved' && (
                        <Button
                          size="sm"
                          className="h-7 px-2 text-xs rounded-sm bg-purple-600 text-white"
                          onClick={() => issueMarksheet(t.id)}
                        >
                          <Award className="h-3 w-3 mr-1" /> Issue
                        </Button>
                      )}
                      {t.status === 'issued' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs rounded-sm border-[#C5A572] text-[#C5A572]"
                          onClick={() => downloadMarksheetPDF(t)}
                          data-testid={`download-marksheet-pdf-${t.id}`}
                        >
                          <Download className="h-3 w-3 mr-1" /> PDF
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* View/Edit Transcript Modal */}
      {selectedTranscript && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTranscript(null)}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Transcript Details</h3>
                  <p className="text-sm text-slate-500">{selectedTranscript.candidate_name}</p>
                  {selectedTranscript.transcript_number && (
                    <p className="text-xs font-mono text-slate-400">{selectedTranscript.transcript_number}</p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTranscript(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Candidate Info */}
                <div className="p-3 bg-blue-50 rounded-sm">
                  <p className="text-xs text-blue-600 font-semibold mb-1">Candidate Information</p>
                  <p className="text-sm text-slate-700">{selectedTranscript.candidate_name}</p>
                  <p className="text-xs text-slate-500">{selectedTranscript.candidate_email}</p>
                  <p className="text-xs text-slate-500 mt-1">Examination: {selectedTranscript.examination || 'N/A'}</p>
                </div>

                {/* Scores */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Theory Score</p>
                    <p className="text-lg font-bold text-[#1B2A4A]">{selectedTranscript.subject_scores?.theory || 0}/100</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">MCQ Score</p>
                    <p className="text-lg font-bold text-[#1B2A4A]">{selectedTranscript.subject_scores?.mcq || 0}/100</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Practical Score</p>
                    <p className="text-lg font-bold text-[#1B2A4A]">{selectedTranscript.practical_score || 0}/100</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">OSCE Score</p>
                    <p className="text-lg font-bold text-[#1B2A4A]">{selectedTranscript.osce_score || 0}/100</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Viva Score</p>
                    <p className="text-lg font-bold text-[#1B2A4A]">{selectedTranscript.viva_score || 0}/100</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-sm">
                    <p className="text-xs text-amber-600">Total Percentage</p>
                    <p className="text-lg font-bold text-amber-700">{selectedTranscript.percentage?.toFixed(1) || 0}%</p>
                  </div>
                </div>

                {/* Examiner Info */}
                {selectedTranscript.examiner_name && (
                  <div className="p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Assessed by</p>
                    <p className="text-sm font-medium text-[#1B2A4A]">{selectedTranscript.examiner_name}</p>
                  </div>
                )}

                {/* Remarks */}
                {selectedTranscript.overall_remarks && (
                  <div className="p-3 bg-blue-50 rounded-sm">
                    <p className="text-xs text-blue-600 font-semibold mb-1">Examiner Remarks</p>
                    <p className="text-sm text-slate-700">{selectedTranscript.overall_remarks}</p>
                  </div>
                )}

                {/* Admin Remarks */}
                {selectedTranscript.admin_remarks && (
                  <div className="p-3 bg-purple-50 rounded-sm">
                    <p className="text-xs text-purple-600 font-semibold mb-1">Admin Remarks</p>
                    <p className="text-sm text-slate-700">{selectedTranscript.admin_remarks}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  {['submitted', 'under_review'].includes(selectedTranscript.status) && (
                    <>
                      <Button
                        className="bg-green-600 text-white rounded-sm"
                        onClick={() => { approveTranscript(selectedTranscript.id); setSelectedTranscript(null); }}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-400 text-red-600 rounded-sm"
                        onClick={() => {
                          const reason = window.prompt('Rejection reason:');
                          if (reason) { rejectMarksheet(selectedTranscript.id, reason); setSelectedTranscript(null); }
                        }}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {selectedTranscript.status === 'approved' && (
                    <Button
                      className="bg-purple-600 text-white rounded-sm"
                      onClick={() => { issueMarksheet(selectedTranscript.id); setSelectedTranscript(null); }}
                    >
                      <Award className="h-4 w-4 mr-1" /> Issue Transcript
                    </Button>
                  )}
                  {selectedTranscript.status === 'issued' && (
                    <Button
                      className="bg-[#C5A572] text-white rounded-sm"
                      onClick={() => downloadMarksheetPDF(selectedTranscript)}
                    >
                      <Download className="h-4 w-4 mr-1" /> Download PDF
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default MarkSheetsTab;
